import { chunkSizes, chunkTypes } from '../chunk';
import AnimationControlChunkView from '../chunks/animationControlChunkView';
import FrameControlChunkView, { BlendOperation, DisposeOperation } from '../chunks/frameControlChunkView';
import FrameDataChunkView from '../chunks/frameDataChunkView';
import ImageHeaderChunkView from '../chunks/imageHeaderChunkView';
import PngChunkView from '../chunks/pngChunkView';
import { flatten, numberToBytes } from '../utils';

export type AnimatedPngMetadata = {
    name: string
    width: number
    height: number
    playTimes: number
};

export interface FrameRenderingInfo
{
    frameNumber: number
    /**
     * x position of the following frame
     */
    x: number
    /**
     * y position of the following frame
     */
    y: number
    /**
     * the numerator of the delay fraction; indicating the time to display the
     * current frame, in seconds.
     */
    delayNumerator: number
    /**
     * the denominator of the delay fraction; indicating the time to display the
     * current frame, in seconds.
     */
    delayDenominator: number
    /**
     * the type of frame area disposal to be done after rendering this frame.
     */
    dispose: DisposeOperation
    /**
     * whether the frame is to be alpha blended into the current output buffer content,
     * or whether it should completely replace its region in the output buffer.
     */
    blend: BlendOperation
}

export interface RawPngFrame
{
    metadata: FrameRenderingInfo
    dataChunks: Uint8Array[]
}

export default class FrameToPngTransformer implements Transformer<PngChunkView, RawPngFrame>
{
    private sequenceNumber: number = 0;
    private isSharedChunksArea = true;
    private headerChunkIndex = -1;
    private resolution: { width: number, height: number } | undefined;
    private readonly sharedChunks: PngChunkView[] = [];
    private currentFrameNumber: number = 0;
    private currentFrameControl: FrameControlChunkView | undefined;
    private currentFrameData: PngChunkView[] = [];
    private animationControlChunk: AnimationControlChunkView | undefined;
    private readonly trailerBuffer: Uint8Array = new Uint8Array([
        0x00,
        0x00,
        0x00,
        0x00,
        ...chunkTypes.trailer,
        0xae,
        0x42,
        0x60,
        0x82,
    ]);

    getAnimationInfo(): Omit<AnimatedPngMetadata, 'name'> | undefined
    {
        if (!this.animationControlChunk || !this.resolution)
        {
            return undefined;
        }

        return {
            ...this.resolution,
            playTimes: this.animationControlChunk.playsNumber,
        };
    }

    start()
    {
        this.sequenceNumber = 0;
        this.headerChunkIndex = -1;
        this.sharedChunks.length = 0;
        this.currentFrameControl = undefined;
        this.currentFrameNumber = 0;
        this.currentFrameData = [];
        this.animationControlChunk = undefined;
    }

    transform(
        chunk: PngChunkView,
        controller: TransformStreamDefaultController<RawPngFrame>,
    )
    {
        const type = chunk.type;

        switch (type)
        {
            case 'acTL':
                this.animationControlChunk = new AnimationControlChunkView(
                    chunk.getRawData(),
                );
                break;
            case 'fcTL':
                this.enqueuePngChunks(controller);

                this.isSharedChunksArea = false;
                this.currentFrameControl = new FrameControlChunkView(chunk.getRawData());
                this.validateSequenceNumber(this.currentFrameControl.sequenceNumber);

                this.currentFrameData = [];
                break;
            case 'fdAT':
            case 'IDAT':
                if (!this.isSharedChunksArea)
                {
                    if (type === 'fdAT')
                    {
                        const frameDataChunk = new FrameDataChunkView(chunk.getRawData());

                        this.validateSequenceNumber(frameDataChunk.sequenceNumber);

                        const sequenceNumberSize = 4;
                        const imageDataSize = frameDataChunk.length - sequenceNumberSize;
                        const lengthBytes = numberToBytes(imageDataSize, chunkSizes.LENGTH);
                        const typeBytes = new Uint8Array(chunkTypes.imageData);
                        const data = frameDataChunk.frameData.getRawChunks();

                        const imageDataChunk = new PngChunkView([
                            lengthBytes,
                            typeBytes,
                            ...data,
                            new Uint8Array(chunkSizes.CRC),
                        ]);

                        imageDataChunk.recalculateCrc();
                        chunk = imageDataChunk;
                    }
                    this.currentFrameData.push(chunk);
                }
                break;
            case 'IHDR': {
                this.headerChunkIndex = this.sharedChunks.length;
                const headerChunk = new ImageHeaderChunkView(chunk.getRawData());

                this.resolution = {
                    width: headerChunk.width,
                    height: headerChunk.height
                };
                this.sharedChunks.push(chunk);
                break;
            }
            case 'IEND':
                this.enqueuePngChunks(controller);
                controller.terminate();
                break;
            default:
                if (this.isSharedChunksArea)
                {
                    this.sharedChunks.push(chunk);
                }
        }
    }

    private validateSequenceNumber(sequenceNumber: number)
    {
        if (this.sequenceNumber !== sequenceNumber)
        {
            throw new Error(`Invalid sequence number (expected: ${this.sequenceNumber}, actual: ${sequenceNumber})`);
        }
        this.sequenceNumber++;
    }

    private enqueuePngChunks(controller: TransformStreamDefaultController<RawPngFrame>)
    {
        if (this.currentFrameControl)
        {
            const isFirstFrame = this.currentFrameNumber === 0;

            const metadata: FrameRenderingInfo = {
                frameNumber: this.currentFrameNumber++,
                x: this.currentFrameControl.x,
                y: this.currentFrameControl.y,
                delayDenominator: this.currentFrameControl.delayDenominator,
                delayNumerator: this.currentFrameControl.delayNumerator,
                blend: this.currentFrameControl.blend,
                dispose: (isFirstFrame && this.currentFrameControl.dispose === DisposeOperation.PREVIOUS)
                    ? DisposeOperation.BACKGROUND
                    : this.currentFrameControl.dispose,
            };

            const pngDataChunks: Uint8Array[] = [new Uint8Array(chunkTypes.signature)];

            for (let i = 0; i < this.sharedChunks.length; i++)
            {
                const sharedChunk = this.sharedChunks[i];
                let byteChunks = sharedChunk.getRawData();

                if (i === this.headerChunkIndex)
                {
                    byteChunks = [flatten(byteChunks)];
                    const frameWidth = this.currentFrameControl.width;
                    const frameHeight = this.currentFrameControl.height;
                    const headerChunkView = new ImageHeaderChunkView(byteChunks);

                    if (frameWidth !== headerChunkView.width || frameHeight !== headerChunkView.height)
                    {
                        headerChunkView.width = this.currentFrameControl.width;
                        headerChunkView.height = this.currentFrameControl.height;
                        headerChunkView.recalculateCrc();
                    }
                }

                for (const byteChunk of byteChunks)
                {
                    pngDataChunks.push(byteChunk);
                }
            }

            for (const dataChunkView of this.currentFrameData)
            {
                const byteChunks = dataChunkView.getRawData();

                for (const byteChunk of byteChunks)
                {
                    pngDataChunks.push(byteChunk);
                }
            }

            pngDataChunks.push(this.trailerBuffer);

            controller.enqueue({
                metadata,
                dataChunks: pngDataChunks
            });
        }
    }
}
